import {
  ChangeEvent,
  forwardRef,
  KeyboardEvent,
  Ref,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import './search-input.scss';
import {
  IconButton,
  InputAdornment,
  InputBase,
  InputBaseProps as MUIInputProps,
} from '@mui/material';
import {Search} from '@mui/icons-material';
import {Pallete} from '../../theme/bluebird';
import {AppContextType} from '../../contexts/app/types';
import {AppContext} from '../../contexts/app/context';
import {ThemeMode} from '../../enums/theme-mode';
import {useLocation} from 'react-router-dom';

interface SearchInputProps extends MUIInputProps {
  onSearchAction: (value: string) => void;
}

const SearchInput = forwardRef(
  ({sx, onSearchAction, ...rest}: SearchInputProps, ref: Ref<HTMLInputElement>) => {
    const [value, setValue] = useState<string | undefined>('');

    const {search} = useLocation();

    const queryParams = useMemo(() => new URLSearchParams(search), [search]);

    const appContext: AppContextType = useContext(AppContext);

    const handleChangeEvent = (event: ChangeEvent<HTMLInputElement>) => {
      setValue(event.target.value);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        onSearchAction(event.currentTarget.value);
      }
    };

    const handleAmplifierClick = () => onSearchAction(value ?? '');

    const getInitialValueFromParams = () => {
      // Get initial search value from URL query parameters
      const locationQueryParams = queryParams.toString().split('&');
      const searchQueryParam = locationQueryParams.find((searchString) =>
        searchString.includes('search')
      );
      const searchQuery = searchQueryParam?.split('=')[1];

      // If found, commit to state and dispatch change
      if (searchQuery) {
        setValue(searchQuery);
        onSearchAction(searchQuery);
      }
    };

    useEffect(() => {
      getInitialValueFromParams();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <InputBase
        inputRef={ref}
        className="search-input"
        value={value}
        onChange={handleChangeEvent}
        onKeyDown={handleKeyDown}
        sx={{
          fontFamily: 'Roboto, sans-serif',
          fontWeight: 400,
          fontSize: '14px',
          height: '40px',
          padding: '0 0 0 15px',
          borderRadius: '5px',
          border: `1px solid ${Pallete.blue.lighter}`,
          backgroundColor:
            appContext.state.currentThemeMode === ThemeMode.Light
              ? Pallete.white.base
              : 'transparent',
          color: Pallete.gray.light,
          '&:hover': {
            color: Pallete.blue.base,
            border: `1px solid ${Pallete.blue.base}`,
          },
          '&.Mui-focused': {
            color: Pallete.blue.base,
            border: `1px solid ${Pallete.blue.base}`,
          },
          ...sx,
        }}
        endAdornment={
          <InputAdornment position="end" sx={{color: 'currentColor'}}>
            <IconButton color="inherit" onClick={handleAmplifierClick}>
              <Search />
            </IconButton>
          </InputAdornment>
        }
        {...rest}
      />
    );
  }
);

export default SearchInput;
